const GLOBAL_TAGGING_CONFIG_ID = 'refapp-global-tagging-configuration';
const DEFAULT_HANDLER_OPTIONS = { capture: true };

export default class EventsTracking {
  /**
   * This class aims to remove code duplication among components by moving most of the tracking
   * logic in one place and exposing generic methods that these components may consume in whatever
   * manner they need.
   * @param {String} taggingId String id used to identify a component and its tracking config
   * @param {Object} [options] Optional settings for the component tracking instance.
   * @param {Object} [options.handlerOptions] Optional per-event configuration for event listener options that will be merged with the default options {@link DEFAULT_HANDLER_OPTIONS}.
   * For example: { click: { capture: false, once: true }, mouseover: { once: true } }
   */
  constructor(taggingId, options = {}) {
    const { handlerOptions = {} } = options;

    const instanceTaggingConfigEl = document.getElementById(taggingId);
    if (!instanceTaggingConfigEl) {
      console.warn(`EventsTracking: No configuration element found for taggingId: ${taggingId}`);
      return;
    }

    const brandLevelTaggingConfigEl = document.querySelector(
      `[data-brand-level-tagging-id="${taggingId}"]`,
    );

    this.taggingConfiguration = tryParseJson(instanceTaggingConfigEl.textContent);
    this.brandLevelTaggingConfiguration = tryParseJson(brandLevelTaggingConfigEl?.textContent);
    this.globalTaggingConfiguration = EventsTracking.getGlobalTaggingConfiguration();

    const trackedEvents = Object.keys(this.taggingConfiguration);

    if (!this.taggingConfiguration || trackedEvents.length === 0) return;

    const taggableComponent = document.querySelector(`[data-refapp-tagging-id="${taggingId}"]`);
    trackedEvents.forEach((eventName) =>
      this.trackerInit(taggableComponent, eventName, handlerOptions[eventName]),
    );
  }

  /**
   * Get the global tagging configuration
   * @returns {Object} Object with the global tagging configuration
   */
  static getGlobalTaggingConfiguration() {
    const globalTaggingConfigEl = document.getElementById(GLOBAL_TAGGING_CONFIG_ID);
    const globalTaggingConfig = tryParseJson(globalTaggingConfigEl?.textContent);

    const { globalVars = {} } = globalTaggingConfig;
    Object.entries(globalVars).forEach(([placeholder, value]) => {
      globalVars[placeholder] = String(value).toLowerCase().trim();
    });

    return globalTaggingConfig;
  }

  /**
   * Get the global variables defined in the global tagging configuration.
   * @returns {Object} An object containing all global variables, or an empty object if not set.
   */
  static get globalVars() {
    return this.getGlobalTaggingConfiguration()?.globalVars || {};
  }

  /**
   * The tracker init function assigns event listeners according to the configuration supplied
   * @param {HTMLElement} taggableComponent Element to which the listener is to be added
   * @param {String} eventName name of the HTML event to attach a listener to
   * @param {Object} handlerOptions HTML event listener options that will be merged with the default options {@link DEFAULT_HANDLER_OPTIONS}
   * @returns {void}
   */
  trackerInit(taggableComponent, eventName, handlerOptions = {}) {
    if (!taggableComponent) return;

    // monitor slider movement events, whichever the user interaction source
    taggableComponent.addEventListener(eventName, this.handleEvent.bind(this, eventName), {
      ...DEFAULT_HANDLER_OPTIONS,
      ...handlerOptions,
    });
  }

  /**
   * Event handler passed to each of the tracked events
   * @param {String} eventName Name of the event that was triggered
   * @param {Event} e Event instance received by the handler function
   * @returns {void}
   */
  handleEvent(eventName, e) {
    const element = e.target.closest('[data-refapp-tagging-target]');
    if (!element) return;

    const taggingTarget = element.dataset.refappTaggingTarget;
    if (!taggingTarget) return;

    const event = this.taggingConfiguration[eventName].find((c) => c.target === taggingTarget);
    const extras = tryParseJson(element.dataset.refappTaggingExtras);
    const brandEvent = this.brandLevelTaggingConfiguration[eventName]?.find(
      (c) => c.target === taggingTarget,
    );

    if (!event) return;

    const payload = {
      ...event.payload,
      ...extras,
      ...brandEvent?.payload,
    };

    const { globalVars = {} } = this.globalTaggingConfiguration;

    for (const placeholder in globalVars) {
      const value = globalVars[placeholder];
      if (!placeholder || !value) continue;

      for (const payloadKey in payload) {
        const payloadValue = payload[payloadKey];
        if (typeof payloadValue === 'string' && payloadValue.includes(placeholder)) {
          payload[payloadKey] = payloadValue.replaceAll(placeholder, value);
        }
      }
    }

    window.dataLayer ||= [];
    window.dataLayer.push(payload);
  }
}

/**
 * Utility function that attempts to parse a JSON string into a JS object
 * @param {String} jsonString String to attempt to parse into a json object
 * @returns {Object}
 */
function tryParseJson(jsonString) {
  try {
    return JSON.parse(jsonString?.trim());
  } catch (error) {
    return {};
  }
}
